/**
 * ΕΚΔΟΣΕΙΣ AGENT — αναβάθμιση του print agent στα ταμεία, από απόσταση.
 *
 * Από την 2.2.0 ο agent αναβαθμίζεται μόνος του όταν ο server του το προσφέρει.
 * Εδώ διαλέγεις ΠΟΙΑ καταστήματα και ΠΟΙΑ έκδοση. Η οθόνη είναι φτιαγμένη ώστε
 * να σπρώχνει προς το ασφαλές:
 *   • ποτέ «όλα» σιωπηλά — κάθε κατάστημα επιλέγεται ρητά, και σε πολλά μαζί
 *     η επιβεβαίωση προτείνει να ξεκινήσεις από ένα
 *   • δείχνει ποια ταμεία ΔΕΝ μπορούν να αναβαθμιστούν ακόμα (παλιός agent χωρίς
 *     αυτο-αναβάθμιση) — εκεί χρειάζεται μία τελευταία επίσκεψη
 *   • δείχνει τις επαναφορές με τον λόγο, και το «ξαναδοκίμασε» είναι ρητή πράξη
 *
 * Νέα εμφάνιση (14/10/2026): σελίδα «Πίνακας» (ListPage + DataTable) αντί για κουτιά. Ίδιες κλήσεις
 * (fleet, rollout, rollout/clear, γενικός διακόπτης), ίδια επιλογή και ίδια επιβεβαίωση με τα ίδια λόγια.
 *
 * 24/10/2026: orgId null = τα καταστήματα ΧΩΡΙΣ οργανισμό (π.χ. GENERIS στον 144, ως τότε στόχος μόνο με script),
 * και «Δημοσίευση έκδοσης» από τον κώδικα του server αντί για ssh (AgentReleasePublish).
 */
import React, { useState, useEffect, useCallback, useId, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { organizationAPI } from '../services/api';
import { CheckCircle, Download, Loader2, Power, RefreshCw, Upload, X } from 'lucide-react';
import { useToast } from './shared/Toast';
import AgentReleasePublish from './AgentReleasePublish';
import { LoadFailed } from './settings/instantSettings';
import {
  ChipTone, Column, DataTable, EmptyState, GHOST_BTN, ICON_BTN, ListPage, Notice, PRIMARY_BTN, Stat, Stats, StatusChip,
} from './shared/listPage';
import { rich } from './home/ui';
import { TFn } from './org/orgFormat';
import './org/org.css';

interface Release {
  version: string;
  sha256: string;
  size_bytes: number;
  git_commit: string | null;
  notes: string | null;
  created_at: string;
}

interface FleetStore {
  store_id: number;
  store_name: string;
  connected: boolean;
  version: string | null;
  selfUpdate: boolean;
  target_version: string | null;
  target_set_at: string | null;
  last_status: string | null;
  last_detail: string | null;
  last_to: string | null;
  last_at: string | null;
}

interface Fleet {
  autoUpdateEnabled: boolean;
  canToggleSwitch: boolean;
  canPublish?: boolean;
  releases: Release[];
  stores: FleetStore[];
}

// Τι σημαίνει κάθε κατάσταση, σε γλώσσα ανθρώπου (κείμενα: orgDashboard:agent.status.*).
const STATUS_TONE: Record<string, ChipTone> = {
  offered: 'muted',
  waiting_idle: 'warn',
  downloading: 'brand',
  verified: 'brand',
  applying: 'brand',
  done: 'ok',
  rolled_back: 'danger',
  failed: 'danger',
  skipped: 'muted',
};
const IN_PROGRESS = ['offered', 'waiting_idle', 'downloading', 'verified', 'applying'];

function cmp(a: string, b: string): number {
  const pa = a.split('.').map(Number), pb = b.split('.').map(Number);
  for (let i = 0; i < 3; i++) { if ((pa[i] || 0) !== (pb[i] || 0)) return (pa[i] || 0) - (pb[i] || 0); }
  return 0;
}

function ago(t: TFn, iso: string | null): string {
  if (!iso) return '';
  const s = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return t('orgDashboard:agent.ago.now');
  if (s < 3600) return t('orgDashboard:agent.ago.minutes', { n: Math.round(s / 60) });
  if (s < 86400) return t('orgDashboard:agent.ago.hours', { n: Math.round(s / 3600) });
  return new Date(iso).toLocaleDateString('el-GR', { timeZone: 'Europe/Athens', day: 'numeric', month: 'short' });
}

const OrgPrintAgentFleet: React.FC<{ orgId: number | null }> = ({ orgId }) => {
  const { t: tRaw } = useTranslation(['orgDashboard', 'common']);
  const t = tRaw as unknown as TFn;
  const { showToast } = useToast();
  const uid = useId();
  const [fleet, setFleet] = useState<Fleet | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<{ forbidden: boolean; message: string } | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [version, setVersion] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState<null | 'rollout' | 'clear'>(null);
  const [publishOpen, setPublishOpen] = useState(false);
  const confirmRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    try {
      const d: Fleet = await organizationAPI.getAgentFleet(orgId);
      setFleet(d);
      setError(null);
      setVersion(v => v || (d.releases[0]?.version ?? ''));
    } catch (e: any) {
      setError({
        forbidden: e?.response?.status === 403,
        message: e?.response?.data?.error || t('orgDashboard:agent.loadFailed'),
      });
    } finally {
      setLoading(false);
    }
  }, [orgId, t]);

  useEffect(() => { load(); }, [load]);

  // Όσο κάποιο ταμείο είναι στη μέση αναβάθμισης, ανανέωση κάθε 10″.
  const anyInProgress = !!fleet?.stores.some(s => s.last_status && IN_PROGRESS.includes(s.last_status) && s.target_version);
  useEffect(() => {
    if (!anyInProgress) return undefined;
    const timer = setInterval(load, 10000);
    return () => clearInterval(timer);
  }, [anyInProgress, load]);

  // Επιβεβαίωση: focus στο «Άκυρο», Esc κλείνει.
  useEffect(() => {
    if (!confirm) return undefined;
    confirmRef.current?.querySelector<HTMLButtonElement>('[data-autofocus]')?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setConfirm(null); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [confirm]);

  const stores = useMemo(() => fleet?.stores || [], [fleet]);
  const sel = useMemo(() => stores.filter(s => selected.has(s.store_id)), [stores, selected]);
  const selNoSelfUpdate = sel.filter(s => s.connected && !s.selfUpdate);
  const selOffline = sel.filter(s => !s.connected);

  const counts = useMemo(() => ({
    total: stores.length,
    selfUpdate: stores.filter(s => s.selfUpdate).length,
    needVisit: stores.filter(s => s.connected && !s.selfUpdate).length,
    offline: stores.filter(s => !s.connected).length,
  }), [stores]);

  const toggle = useCallback((id: number) => setSelected(p => {
    const n = new Set(p);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  }), []);
  const allOn = stores.length > 0 && stores.every(s => selected.has(s.store_id));

  const storesWord = (n: number) => t('orgDashboard:agent.stores', { count: n });

  const doRollout = async () => {
    setConfirm(null); setBusy(true);
    try {
      const r = await organizationAPI.setAgentRollout(orgId, Array.from(selected), version);
      showToast('success', t('orgDashboard:agent.rolloutDone', { version, stores: storesWord(r.storeIds.length), offered: r.offeredNow }));
      setSelected(new Set());
      await load();
    } catch (e: any) {
      showToast('error', e?.response?.data?.error || t('orgDashboard:agent.failed'));
    } finally { setBusy(false); }
  };

  const doClear = async () => {
    setConfirm(null); setBusy(true);
    try {
      const r = await organizationAPI.clearAgentRollout(orgId, Array.from(selected));
      showToast('success', t('orgDashboard:agent.clearDone', { count: r.storeIds.length }));
      setSelected(new Set());
      await load();
    } catch (e: any) {
      showToast('error', e?.response?.data?.error || t('orgDashboard:agent.failed'));
    } finally { setBusy(false); }
  };

  const toggleSwitch = async () => {
    if (!fleet) return;
    setBusy(true);
    try {
      await organizationAPI.setAgentAutoUpdate(!fleet.autoUpdateEnabled);
      await load();
    } catch (e: any) {
      showToast('error', e?.response?.data?.error || t('orgDashboard:agent.failed'));
    } finally { setBusy(false); }
  };

  // Μόλις δημοσιευτεί, η νέα έκδοση γίνεται η επιλογή της μπάρας — αυτήν θέλεις να στοχεύσεις μετά.
  const onPublished = async (v: string) => {
    setPublishOpen(false);
    setVersion(v);
    await load();
  };

  const newest = fleet?.releases[0]?.version;

  const columns = useMemo<Column<FleetStore>[]>(() => [
    {
      key: 'pick',
      header: (
        <input
          type="checkbox"
          className="org-pick"
          checked={allOn}
          onChange={() => setSelected(allOn ? new Set() : new Set(stores.map(s => s.store_id)))}
          aria-label={t('orgDashboard:agent.selectAll')}
          data-testid="org-agent-select-all"
        />
      ),
      width: '52px',
      stack: 'none',
      render: s => (
        <input
          type="checkbox"
          className="org-pick"
          checked={selected.has(s.store_id)}
          onChange={() => toggle(s.store_id)}
          aria-label={t('orgDashboard:agent.selectStore', { name: s.store_name })}
          data-testid={`org-agent-pick-${s.store_id}`}
        />
      ),
    },
    {
      key: 'store',
      header: t('orgDashboard:agent.colStore'),
      width: 'minmax(180px, 1.6fr)',
      stack: 'title',
      render: s => <span className="font-medium">{s.store_name}</span>,
      stackRender: s => s.store_name,
    },
    {
      key: 'current',
      header: t('orgDashboard:agent.colCurrent'),
      width: 'minmax(170px, 1.2fr)',
      render: s => {
        const upToDate = !!s.version && !!newest && cmp(s.version, newest) >= 0;
        return (
          <span className="inline-flex items-center gap-2 flex-wrap">
            <span className={`org-dot ${s.connected ? 'is-on' : ''}`} aria-hidden="true" />
            <span className={s.connected ? 'text-t1' : 'text-t4'} title={s.connected ? t('orgDashboard:agent.connected') : t('orgDashboard:agent.offline')}>
              {s.connected ? (s.version || t('orgDashboard:agent.legacy')) : '—'}
            </span>
            {s.connected && !s.selfUpdate && <StatusChip tone="warn">{t('orgDashboard:agent.needsVisit')}</StatusChip>}
            {upToDate && <CheckCircle className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" aria-label={t('orgDashboard:agent.upToDate')} />}
          </span>
        );
      },
      stackRender: s => (s.connected ? (s.version || t('orgDashboard:agent.legacy')) : t('orgDashboard:agent.offline')),
    },
    {
      key: 'target',
      header: t('orgDashboard:agent.colTarget'),
      width: '110px',
      render: s => (s.target_version ? <span className="text-t2">{s.target_version}</span> : <span className="text-t4">—</span>),
      stackRender: s => (s.target_version ? t('orgDashboard:agent.targetShort', { version: s.target_version }) : null),
    },
    {
      key: 'status',
      header: t('common:status'),
      width: 'minmax(220px, 1.8fr)',
      render: s => {
        const st = s.last_status && s.target_version ? s.last_status : null;
        if (!st) return <span className="text-t4">—</span>;
        const tone = STATUS_TONE[st] || 'muted';
        return (
          <div className="min-w-0">
            <span className="inline-flex items-center gap-2 flex-wrap">
              <StatusChip tone={tone}>
                {st === 'applying' && <Loader2 className="w-3 h-3 mr-1 animate-spin" aria-hidden="true" />}
                {t(`orgDashboard:agent.status.${st}`, { defaultValue: st })}{s.last_to ? ` ${s.last_to}` : ''}
              </StatusChip>
              <span className="text-xs text-t4">{ago(t, s.last_at)}</span>
            </span>
            {s.last_detail && (st === 'rolled_back' || st === 'failed' || st === 'skipped') && (
              <span className="block text-xs text-t3 mt-1 max-w-md">{s.last_detail}</span>
            )}
          </div>
        );
      },
      stackRender: s => (s.last_status && s.target_version ? t(`orgDashboard:agent.status.${s.last_status}`, { defaultValue: s.last_status }) : null),
    },
  ], [t, allOn, stores, selected, toggle, newest]);

  const header = fleet ? (
    <>
      <StatusChip tone={fleet.autoUpdateEnabled ? 'ok' : 'danger'} testId="org-agent-switch-state">
        {fleet.autoUpdateEnabled ? t('orgDashboard:agent.switchOn') : t('orgDashboard:agent.switchOff')}
      </StatusChip>
      {fleet.canToggleSwitch && (
        <button type="button" onClick={toggleSwitch} disabled={busy} title={t('orgDashboard:agent.switchTitle')} className={GHOST_BTN} data-testid="org-agent-switch">
          <Power className="w-4 h-4" aria-hidden="true" /> {fleet.autoUpdateEnabled ? t('orgDashboard:agent.stopAll') : t('orgDashboard:agent.enable')}
        </button>
      )}
      {fleet.canPublish && (
        <button type="button" onClick={() => setPublishOpen(true)} disabled={busy} className={GHOST_BTN} data-testid="org-agent-publish-open">
          <Upload className="w-4 h-4" aria-hidden="true" /> {t('orgDashboard:agent.publish.title')}
        </button>
      )}
      <button type="button" onClick={load} className={ICON_BTN} title={t('common:refresh')} aria-label={t('common:refresh')} data-testid="org-agent-refresh">
        <RefreshCw className="w-4 h-4" aria-hidden="true" />
      </button>
    </>
  ) : null;

  return (
    <ListPage
      eyebrow={t('orgDashboard:nav.groups.org')}
      title={t('orgDashboard:agent.title')}
      subtitle={t('orgDashboard:agent.subtitle')}
      actions={header}
      testId="org-agent"
    >
      {error && !fleet ? (
        <div className="org-agent-top">
          <LoadFailed message={error.forbidden ? t('orgDashboard:agent.forbidden') : error.message} onRetry={() => { setLoading(true); load(); }} />
        </div>
      ) : (
        <>
          {fleet && (
            <div className="org-agent-top">
              <Stats testId="org-agent-stats">
                <Stat label={t('orgDashboard:agent.statSelfUpdate')} value={counts.selfUpdate} />
                <Stat label={t('orgDashboard:agent.statNeedVisit')} value={counts.needVisit} />
                <Stat label={t('orgDashboard:agent.statOffline')} value={counts.offline} />
                <Stat label={t('orgDashboard:agent.statReleases')} value={fleet.releases.length} />
              </Stats>
              {!fleet.autoUpdateEnabled && (
                <Notice tone="danger" testId="org-agent-switch-closed">{t('orgDashboard:agent.switchClosedNote')}</Notice>
              )}
              {counts.needVisit > 0 && (
                <Notice testId="org-agent-need-visit">
                  <span>{rich(t('orgDashboard:agent.needVisitNote', { count: counts.needVisit }))}</span>
                </Notice>
              )}
            </div>
          )}

          <DataTable
            columns={columns}
            rows={stores}
            rowKey={s => s.store_id}
            onRowClick={s => toggle(s.store_id)}
            loading={loading && !fleet}
            rowClassName={s => (selected.has(s.store_id) ? 'org-picked' : undefined)}
            rowLabel={s => t('orgDashboard:agent.rowLabel', { name: s.store_name })}
            empty={<EmptyState title={t(orgId === null ? 'orgDashboard:agent.emptyNoOrg' : 'orgDashboard:agent.empty')} />}
            testId="org-agent-table"
          />
        </>
      )}

      {/* Μπάρα ενεργειών */}
      {fleet && selected.size > 0 && (
        <div className="org-actionbar" role="region" aria-label={t('orgDashboard:agent.actionsLabel')} data-testid="org-agent-actions">
          <div className="org-actionbar-count">
            <p className="text-sm text-t2"><strong className="text-t1">{selected.size}</strong> {t('orgDashboard:agent.storesNoun', { count: selected.size })}</p>
            <button type="button" onClick={() => setSelected(new Set())} className={ICON_BTN} aria-label={t('orgDashboard:agent.clearSelection')} title={t('orgDashboard:agent.clearSelection')}>
              <X className="w-4 h-4" aria-hidden="true" />
            </button>
          </div>
          <div className="org-actionbar-actions">
            <select
              value={version}
              onChange={e => setVersion(e.target.value)}
              aria-label={t('orgDashboard:agent.version')}
              className="lp-select"
              data-testid="org-agent-version"
            >
              {fleet.releases.map(r => <option key={r.version} value={r.version}>{r.version}</option>)}
            </select>
            <button type="button" onClick={() => setConfirm('clear')} disabled={busy} className={GHOST_BTN} data-testid="org-agent-clear">
              {t('orgDashboard:agent.clearTarget')}
            </button>
            <button type="button" onClick={() => setConfirm('rollout')} disabled={busy || !version} className={PRIMARY_BTN} data-testid="org-agent-rollout">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : <Download className="w-4 h-4" aria-hidden="true" />}
              {t('orgDashboard:agent.upgradeTo', { version })}
            </button>
          </div>
        </div>
      )}

      {/* Επιβεβαίωση — λέει τι θα γίνει */}
      {confirm && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4" onClick={() => setConfirm(null)} data-testid="org-agent-confirm">
          <div
            ref={confirmRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={`${uid}-confirm-title`}
            className="bg-surface-1 border border-line rounded-2xl shadow-xl max-w-md w-full p-6"
            onClick={e => e.stopPropagation()}
          >
            {confirm === 'rollout' ? (
              <>
                <h2 id={`${uid}-confirm-title`} className="text-lg font-bold text-t1 mb-3">{t('orgDashboard:agent.upgradeTo', { version })}</h2>
                <ul className="text-sm text-t2 space-y-2 mb-4 list-disc pl-5">
                  <li>{rich(t('orgDashboard:agent.confirmWhen', { count: sel.length }))}</li>
                  <li>{t('orgDashboard:agent.confirmPause')}</li>
                  <li>{t('orgDashboard:agent.confirmRollback')}</li>
                  {selNoSelfUpdate.length > 0 && (
                    <li className="text-amber-800 dark:text-amber-300">{t('orgDashboard:agent.confirmNoSelfUpdate', { count: selNoSelfUpdate.length })}</li>
                  )}
                  {selOffline.length > 0 && (
                    <li className="text-t3">{t('orgDashboard:agent.confirmOffline', { count: selOffline.length })}</li>
                  )}
                </ul>
                {sel.length > 1 && (
                  <div className="mb-4">
                    <Notice testId="org-agent-one-first">{rich(t('orgDashboard:agent.oneFirst'))}</Notice>
                  </div>
                )}
                <div className="flex justify-end gap-2">
                  <button type="button" onClick={() => setConfirm(null)} className={GHOST_BTN} data-autofocus>{t('orgDashboard:actions.cancel')}</button>
                  <button type="button" onClick={doRollout} className={PRIMARY_BTN} data-testid="org-agent-rollout-confirm">{t('orgDashboard:agent.confirmYes')}</button>
                </div>
              </>
            ) : (
              <>
                <h2 id={`${uid}-confirm-title`} className="text-lg font-bold text-t1 mb-3">{t('orgDashboard:agent.clearTarget')}</h2>
                <p className="text-sm text-t2 mb-4">{t('orgDashboard:agent.clearConfirm', { count: sel.length })}</p>
                <div className="flex justify-end gap-2">
                  <button type="button" onClick={() => setConfirm(null)} className={GHOST_BTN} data-autofocus>{t('orgDashboard:actions.cancel')}</button>
                  <button type="button" onClick={doClear} className={PRIMARY_BTN} data-testid="org-agent-clear-confirm">{t('orgDashboard:agent.clearTarget')}</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {publishOpen && <AgentReleasePublish onClose={() => setPublishOpen(false)} onPublished={onPublished} />}
    </ListPage>
  );
};

export default OrgPrintAgentFleet;
